import { Chip, useMediaQuery, Tooltip, Button, Menu } from "@mui/material";
import {
  isVariationOfAll,
  FILTER_TYPES,
  createAllProcessGroup,
} from "./filtersDrawerUtils";
import {
  BREAKPOINT_TABLET,
  FONT_SIZE_EXTRA_SMALL,
  PNL_COLORS,
} from "../../../constants/constants";
import type { TrendsPageFilterStateType } from "../../../types/types";
import styled from "styled-components/macro";
import { startCase } from "lodash";
import { parseTrendsPageFilterState } from "../trendsPageUtils";
import { useDispatch } from "react-redux";
import {
  updateTrendsPageFilterState,
  clearAllTrendChartBars,
  legacyInitialState,
} from "providers/GlobalStateProvider";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  useTrendsPageFilterState,
  useVisibleGroups,
} from "utils/customHooks/genericHooks";
import FilterIcon from "../../../svgs/FilterIcon";
import { useIsDarkMode, useWorkScheduleConfigJsonListAtom } from "store/atoms";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useState } from "react";
import FilterByGroupDropdownItems from "./FilterByGroupDropdown";
import FilterByProcessDropdownItems from "./FilterByProcessDropdown";
import FilterByShiftDropdownItems from "./FilterByShiftDropdown";
import FilterByTagDropdownItems from "./FilterByTagDropdown";
import FilterByWorkScheduleDropdownItems from "./FilterByWorkScheduleDropdown";
import { ArrowDropDown } from "@mui/icons-material";
import { FilterStateDisplayStyles } from "./FilterStateDisplayStyles";

export const RESET_ALL_FILTERS = "RESET_ALL_FILTERS";

const FilterStateDisplay = () => {
  const visibleGroups = useVisibleGroups();
  const { trendsPageFilterState } = useTrendsPageFilterState();
  const filteredProcessId = trendsPageFilterState.process.id;

  const processGroupOfFilteredProcess = visibleGroups.find(
    ({ processes, longName }) =>
      Boolean(
        processes.find(
          ({ id }) =>
            // find the matching process group
            id === filteredProcessId &&
            // but skip the "All" group
            longName &&
            !isVariationOfAll(longName)
        )
      )
  );
  // we display the group name on the filter Chip
  const filteredProcessGroupName = processGroupOfFilteredProcess
    ? processGroupOfFilteredProcess.longName
    : "";
  const [workScheduleConfigList] = useWorkScheduleConfigJsonListAtom();
  const isCustomerWithMultiWorkSchedules = workScheduleConfigList.length > 1;
  const filterStateArrayDisplay = getFilterStateArrayDisplay(
    trendsPageFilterState,
    filteredProcessGroupName
  ).filter(
    filter =>
      isCustomerWithMultiWorkSchedules ||
      filter.filterType !== FILTER_TYPES.workScheduleId
  );

  const { isFiltered } = parseTrendsPageFilterState({
    trendsPageFilterState,
  });

  const [isDarkMode] = useIsDarkMode();

  return (
    <FilterStateDisplayStyles $isDarkMode={isDarkMode} $isFiltered={isFiltered}>
      <div className="btnFilters" aria-label="FiltersDrawer">
        <FilterIcon />
      </div>
      <div className="filtersWrapper">
        {filterStateArrayDisplay.map(({ filterType, filterValue }) => {
          return (
            <FilterChipDropdown
              key={filterType}
              filterType={filterType}
              filterValue={filterValue}
            />
          );
        })}
        <BtnClearAllFilters disabled={!isFiltered} />
      </div>
    </FilterStateDisplayStyles>
  );
};

export const BtnClearAllFilters = ({ disabled }) => {
  const getResetFilterState = useGetResetFilterState();
  const { translate } = useFlightTranslation();
  return (
    <Tooltip title={disabled ? "" : "Clear all filters"}>
      <Button
        className={
          "btnClearAllFilters doNotPrint" + (disabled ? " disabled" : "")
        }
        size="small"
        disabled={disabled}
        onClick={getResetFilterState(RESET_ALL_FILTERS)}
      >
        {translate("Clear All")}
      </Button>
    </Tooltip>
  );
};

const ChipLabelStyles = styled.div<{ $isPnlCategoryFilter }>`
  position: relative;

  .filterValue {
    display: flex;
    align-items: baseline;
    font-size: 15px;
    text-transform: capitalize;
  }
  .filterTypeLabel {
    font-size: ${FONT_SIZE_EXTRA_SMALL}px;
    position: absolute;
    background: white;
    top: -19px;
    left: -1px;
    color: currentcolor;
    padding: 0 2px;
    text-transform: capitalize;
  }
  .dropdownIcon {
    position: absolute;
    right: 0;
    transform: translate(26px, -7px) scaleX(0.8);
  }
`;

const PnlColorSquareStyles = styled.div<{ $pnlCategory: string }>`
  box-sizing: border-box;
  max-width: 12px;
  max-height: 12px;
  min-width: 12px;
  min-height: 12px;
  border: 1px solid black;
  background: ${props => PNL_COLORS[props.$pnlCategory]};
  margin-right: 0.5ch;
  transform: translateY(1px);
`;

function ChipLabel({
  filterType,
  filterValue,
  filterPnlCategory,
  active,
}: {
  filterType: string;
  filterValue: string;
  filterPnlCategory: string | null;
  active?: boolean;
}) {
  const isPnlCategoryFilter = filterType === FILTER_TYPES.pnlCategory;
  const isTagFilter = filterType === FILTER_TYPES.tag;
  const { trendsPageFilterState } = useTrendsPageFilterState();
  const filterTypeLabel =
    filterType === FILTER_TYPES.shiftName
      ? "Shift"
      : isPnlCategoryFilter
        ? "Loss category"
        : active && filterType === FILTER_TYPES.tag && filterPnlCategory
          ? trendsPageFilterState.tag &&
            isVariationOfAll(trendsPageFilterState.tag)
            ? "Category"
            : `Tag - ${startCase(filterPnlCategory)}`
          : filterType === FILTER_TYPES.workScheduleId
            ? "Work Schedule"
            : filterType;

  const visibleGroups = useVisibleGroups();
  // if process filter is active, we implicitly have also filtered by this process's group
  const isProcessFilterActive =
    trendsPageFilterState.process.name &&
    !isVariationOfAll(trendsPageFilterState.process.name);
  const implicitlySelectedGroup =
    filterTypeLabel === "group" && isProcessFilterActive
      ? visibleGroups.find(group =>
          group.processes.find(
            p => p.name === trendsPageFilterState.process.name
          )
        )
      : null;

  const [workSchedulesConfigList] = useWorkScheduleConfigJsonListAtom();
  const filterValueDisplay =
    filterType === FILTER_TYPES.workScheduleId
      ? `${filterValue} - ${
          workSchedulesConfigList.find(s => s.id === filterValue)?.name
        }`
      : filterValue;
  return (
    <ChipLabelStyles $isPnlCategoryFilter={isPnlCategoryFilter}>
      {filterTypeLabel && (active || implicitlySelectedGroup) && (
        <div className={`filterTypeLabel`}>{filterTypeLabel}</div>
      )}
      <div className={`filterValue ${active ? "" : "inactive"}`}>
        {active && filterPnlCategory && (isPnlCategoryFilter || isTagFilter) ? (
          <>
            <PnlColorSquareStyles $pnlCategory={filterPnlCategory} />
            <div className="filterText">{startCase(filterValue)}</div>
          </>
        ) : !active ? (
          (implicitlySelectedGroup?.shortName ??
          implicitlySelectedGroup?.longName ??
          filterTypeLabel)
        ) : (
          filterValueDisplay
        )}
        {active ? null : <ArrowDropDown className="dropdownIcon" />}
      </div>
    </ChipLabelStyles>
  );
}

export default FilterStateDisplay;

export function getFilterStateArrayDisplay(
  trendsPageFilterState: TrendsPageFilterStateType,
  filteredProcessGroupName: string
): {
  filterType: string;
  filterValue: string;
  groupName?: string;
}[] {
  return [
    {
      filterType: FILTER_TYPES.group,
      filterValue: trendsPageFilterState.group,
    },
    {
      filterType: FILTER_TYPES.process,
      filterValue: trendsPageFilterState.process.name,
      groupName: filteredProcessGroupName,
    },
    {
      filterType: FILTER_TYPES.tag,
      filterValue:
        trendsPageFilterState.tag && isVariationOfAll(trendsPageFilterState.tag)
          ? (trendsPageFilterState.pnlCategory ?? trendsPageFilterState.tag)
          : trendsPageFilterState.tag,
    },
    {
      filterType: FILTER_TYPES.shiftName,
      filterValue: trendsPageFilterState.shiftName,
    },
    {
      filterType: FILTER_TYPES.workScheduleId,
      filterValue: trendsPageFilterState.workScheduleId,
    },
    // * pnl category filter is handled by tag filter dropdown
    // // hide the pnl category filter when there's also a tag filter applied
    // // (we only want to show one or the other)
    // ...(isTrendsPageFilteredByTag
    //   ? []
    //   : [
    //       {
    //         filterType: FILTER_TYPES.pnlCategory,
    //         filterValue: trendsPageFilterState.pnlCategory,
    //       },
    //     ]),
  ];
}

export function useGetResetFilterState() {
  const dispatch = useDispatch();
  const { trendsPageFilterState } = useTrendsPageFilterState();
  const ckey = useCustomerKey();

  return (filterType: string) => () => {
    // reset the filter on this filterType back to its default value
    const allProcess = createAllProcessGroup(ckey).processes[0];
    const newFilterState: TrendsPageFilterStateType =
      filterType === RESET_ALL_FILTERS
        ? legacyInitialState.trendsPageFilterState
        : // to reset a tag filter, also clear the pnl category
          filterType === FILTER_TYPES.tag
          ? //
            {
              ...trendsPageFilterState,
              tag: "All",
              pnlCategory: "All",
            }
          : {
              ...trendsPageFilterState,
              [filterType]: filterType === "process" ? allProcess : "All",
            };
    // save this in the store
    dispatch(updateTrendsPageFilterState({ newFilterState }));
    // if removing the shift filter, remove all shiftly trend chart aggregates
    // so we don't accidentally render one bar per shift
    if ([RESET_ALL_FILTERS, FILTER_TYPES.shiftName].includes(filterType)) {
      dispatch(clearAllTrendChartBars());
    }
  };
}

const DisabledTooltip = ({ children, isDisabled }) =>
  isDisabled ? (
    <Tooltip title="* filtering by shift is not available in the 24 hr range">
      {children}
    </Tooltip>
  ) : (
    <>{children}</>
  );

function FilterChipDropdown({
  filterType,
  filterValue,
}: {
  filterType: string;
  filterValue: string;
}) {
  const isTabletOrLarger = useMediaQuery(`(min-width: ${BREAKPOINT_TABLET}px)`);
  const isFilterActive = filterValue && !isVariationOfAll(filterValue);
  const { trendsPageFilterState } = useTrendsPageFilterState();

  const getResetFilterState = useGetResetFilterState();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isDisabled = false; // currently unused

  return (
    <>
      <DisabledTooltip key={filterType} isDisabled={isDisabled}>
        <Chip
          onClick={e => {
            setAnchorEl(e.currentTarget);
          }}
          className={`trendsFilterChip${
            isDisabled ? " disabled" : ""
          } ${filterType} ${isFilterActive ? "active" : ""}`}
          size={isTabletOrLarger ? "medium" : "small"}
          label={
            <ChipLabel
              filterType={filterType}
              filterValue={`${isDisabled ? "* " : ""}${filterValue}`}
              filterPnlCategory={trendsPageFilterState.pnlCategory}
              active={Boolean(isFilterActive)}
            />
          }
          onDelete={
            filterType === FILTER_TYPES.workScheduleId
              ? undefined
              : isFilterActive
                ? getResetFilterState(filterType)
                : undefined
          }
        />
      </DisabledTooltip>
      <Menu
        disableScrollLock={true}
        disablePortal={true}
        id={`menu-filter-${filterType}`}
        anchorEl={anchorEl}
        keepMounted={false}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
        }}
      >
        <FilterMenuItems
          {...{
            filterType,
            handleClose: () => {
              setAnchorEl(null);
            },
          }}
        />
      </Menu>
    </>
  );
}
const FilterMenuItems = ({ filterType, handleClose }) => {
  switch (filterType) {
    case FILTER_TYPES.group:
      return <FilterByGroupDropdownItems {...{ handleClose }} />;
    case FILTER_TYPES.process:
      return <FilterByProcessDropdownItems {...{ handleClose }} />;
    case FILTER_TYPES.shiftName:
      return <FilterByShiftDropdownItems {...{ handleClose }} />;
    case FILTER_TYPES.tag:
    case FILTER_TYPES.pnlCategory:
      return <FilterByTagDropdownItems {...{ handleClose }} />;
    case FILTER_TYPES.workScheduleId:
      return <FilterByWorkScheduleDropdownItems {...{ handleClose }} />;
    default:
      return null;
  }
};
